import styles from './contract-information.module.scss';
import { Col, Row } from 'reactstrap';
import CompanySearch from '../../company-search/company-search';
import { useSingleContractDispatch } from '../contract-details-reducer/contract-details-reducer';
import TextField, { Autocomplete, MultiLineTextField } from '../../text-field/text-field';
import { PrimaryButton } from '../../button/button';
import Card, { CardContent } from '../../card/card';
import { Typography } from '@frontend-workspace/react-shared-lib';
import { format } from 'date-fns';
import { IContactType, IContractInfo, IInvoiceInfo, IUserAccount } from '../../common-types/common-types';
import { t } from 'i18next';

/* eslint-disable-next-line */
export interface ContractInformationProps {
  contractInfo: IContractInfo,
  invoiceInfo: IInvoiceInfo,
  companyName: string,
  companyList: Array<IContactType>,
  contractEmployeeList: Array<IUserAccount>,
  invoiceEmployeeList: Array<IUserAccount>,
  onContractInfoChange: () => void
}

export function ContractInformation(props: ContractInformationProps) {
  return (<>
    <div className="d-flex flex-row flex-wrap mb-3">
      <div className='mb-3 mb-lg-0 me-3'>
        <TextField
          label={t("standard.start")}
          value={`${format(new Date(props.contractInfo.activationDate), 'dd-MM-yyyy')}`}
          disabled
        />
      </div>
      <div className='mb-3 mb-lg-0 me-3'>
        <TextField
          label={t("standard.duration")}
          value={`${props.contractInfo.invoiceInterval} måneder`}
          disabled
        />
      </div>
      <div className='mb-3 mb-lg-0 me-3'>
        <TextField
          label={t("standard.currency")}
          value={`${props.contractInfo.currency}`}
          disabled
        />
      </div>
      <div className='mb-3 mb-lg-0'>
        <TextField
          label={t("standard.renewalDate")}
          value={`${format(new Date(props.contractInfo.nextInvoicingDate), 'dd-MM-yyyy')}`}
          disabled
        />
      </div>
    </div>
    <Row>
      <Col md={12} xl={6}>
        <ContractInfoCard
          companyList={props.companyList}
          employeeList={props.contractEmployeeList}
          contractInfo={props.contractInfo}
          companyName={props.companyName}
        />
      </Col>
      <Col md={12} xl={6}>
        <InvoiceInfoCard
          companyList={props.companyList}
          employeeList={props.invoiceEmployeeList}
          invoiceInfo={props.invoiceInfo}
        />
      </Col>
    </Row>
    {/* TODO commented out in case we want to roll back from SalesForce. Remove if this is not needed */}
    {/* <div className='mb-3'>
      <PrimaryButton
        variant='contained'
        onClick={props.onContractInfoChange}>
        {t("standard.updateContract")}
      </PrimaryButton>
    </div> */}
  </>);
}

function ContractInfoCard(props: {
  contractInfo: IContractInfo,
  companyList: Array<IContactType>,
  companyName: string,
  employeeList: Array<IUserAccount>
}) {
  const dispatch = useSingleContractDispatch();
  //const handleContractResponsibleChange = (event: SelectChangeEvent) => dispatch({ type: "CONTRACT_INFO_SELECTED_RESPONSIBLE_CHANGED", newResponsibleEmail: event.target.value })
  const handleContractResponsibleChange = (email: string) => dispatch({ type: "CONTRACT_INFO_SELECTED_RESPONSIBLE_CHANGED", newResponsibleEmail: email })
  const handleTextNoteChange = (event: any) => dispatch({ type: "CONTRACT_INFO_NOTE_CHANGE", newValue: event.target.value });
  console.log(props.contractInfo);
  return (<>
    <Card className='mb-4'>
      <CardContent>
        <Typography
          variant="h4"
          component="div"
          className="mb-4">
          {t("standard.contractInformation")}
        </Typography>
        <Row>
          <Col md={12} xxl={6}>
            <div className='mb-3'>
              <TextField
                value={props.companyName}
                label={t("standard.customerNumber")}
                fullWidth
                disabled
              />
            </div>
            <div className='mb-3'>
              {props.employeeList && props.employeeList.length > 0 &&
                <Autocomplete
                  value={props.employeeList.find(e => e.email === props.contractInfo.contractResponsibleEmail) || { firstName: props.contractInfo.contractResponsibleEmail, lastName: `(${t("standard.missingAccount")})` }}
                  getOptionLabel={(option: any) => `${option.firstName} ${option.lastName}` || "ukjent"}
                  options={props.employeeList}
                  renderInput={(params: any) => <TextField
                    {...params}
                    fullWidth
                    label={t("standard.contractManager")}
                    sx={{ minWidth: '320px' }}
                  />
                  }
                  renderOption={(props: any, option: any) => <li {...props} key={option.email}>
                    {option.firstName} {option.lastName}  ({option.email})
                  </li>

                  }
                  onChange={(event: any, userAccount: any) => {
                    handleContractResponsibleChange(userAccount?.email)
                  }}
                />}
            </div>
          </Col>
          <Col md={12} xxl={6}>
            <MultiLineTextField
               label={t("standard.note")}
              fullWidth
              minRows={5}
              value={props.contractInfo.note || ""}
              onChange={handleTextNoteChange}
            />
          </Col>
        </Row>
      </CardContent >
    </Card>
  </>)
}

function InvoiceInfoCard(props: {
  invoiceInfo: IInvoiceInfo,
  companyList: Array<IContactType>,
  employeeList: Array<IUserAccount>
}) {
  const dispatch = useSingleContractDispatch();
  const handleInvoiceResponsibleChange = (name: string) => dispatch({ type: "INVOICE_INFO_SELECTED_RESPONSIBLE_CHANGED", name: name });
  const handleContractCompanyChange = (companyNumber: string) => {
    const newCompany = props.companyList.find(c => c.number === companyNumber);
    if (!newCompany) {
      //TODO no longer needed after SF transition
      //dispatch({ type: "INVOICE_INFO_SELECTED_COMPANY_CHANGED", newCompanyId: companyNumber, newCompanyNumber: "" });
    }
    else dispatch({ type: "INVOICE_INFO_SELECTED_COMPANY_CHANGED", newCompanyId: newCompany.id, newCompanyNumber: newCompany.number || "" });
  }
  const handleTextNoteChange = (event: any) => dispatch({ type: "INVOICE_INFO_NOTE_CHANGE", newValue: event.target.value });
  const handleReferenceChange = (event: any) => dispatch({ type: "INVOICE_INFO_REFERENCE_CHANGE", newValue: event.target.value });

  const invoiceResponsibleName = !props.invoiceInfo.invoiceResponsibleName ?
    { firstName: "Bruker kontraktansvarlig", lastName: "" } :
    props.employeeList.find(e => `${e.firstName} ${e.lastName}` === props.invoiceInfo.invoiceResponsibleName);

  return <Card className='mb-4'>
    <CardContent>
      <Typography
        variant="h4"
        component="div"
        className="mb-4">
        {t("standard.invoiceInformation")}
      </Typography>
      <Row>
        <Col md={12} xxl={6}>
          <div className='mb-3'>
            <label>{t("standard.customerNumber")}</label>
            <CompanySearch
              selectedCompanyNumber={props.invoiceInfo.invoicedCompanyNumber}
              companyList={props.companyList}
              onChange={handleContractCompanyChange}
            />
          </div>
          <div className='mb-3'>
            {props.employeeList && props.employeeList.length > 0 && <Autocomplete
              value={invoiceResponsibleName || { firstName: props.invoiceInfo.invoiceResponsibleName, lastName: `(${t("standard.missingInSuperOffice")})` }}
              getOptionLabel={(option: any) => `${option.firstName} ${option.lastName}` || ""}
              options={props.employeeList}
              renderInput={(params: any) => {
                return <TextField
                  {...params}
                  fullWidth
                  label={t("standard.invoiceResponsible")}
                  sx={{ minWidth: '320px' }}
                />
              }}
              onChange={(event: any, userAccount: any) => {
                var name = userAccount ? `${userAccount?.firstName} ${userAccount?.lastName}` : "";
                handleInvoiceResponsibleChange(name);
              }}
            />}
          </div>
          <div className='mb-3'>
            <TextField
              label={t("standard.customersOrderNumber")}
              value={props.invoiceInfo.reference || ""}
              onChange={handleReferenceChange}
              fullWidth
            />
          </div>
        </Col>
        <Col md={12} xxl={6}>
          <MultiLineTextField
             label={t("standard.note")}
            fullWidth
            minRows={5}
            value={props.invoiceInfo.note || ""}
            onChange={handleTextNoteChange}
          />
        </Col>
      </Row>
    </CardContent>
  </Card>
};

export default ContractInformation;